/**
 * CALENDAR VIEW COMPONENT
 * 
 * Monthly stamp calendar visualization
 * Shows daily stamps, milestones, and rewards
 * Zero guilt language - rewards presence, not perfection
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CheckCircle2, Star, Gift, Award, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { MonthlyCalendar, DailyStamp, CalendarConfig, User } from '../types';
import { getCurrentCalendar, getCalendarStats, hasStampedToday, addDailyStamp, getTodayKey } from '../services/calendarService';

interface CalendarViewProps {
  user: User;
  onStampAdded?: (xpEarned: number) => void;
}

export const CalendarView = ({ user, onStampAdded }: CalendarViewProps) => {
  const { t } = useTranslation();
  const [calendar, setCalendar] = useState<MonthlyCalendar | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [reflectionText, setReflectionText] = useState('');

  // Get today's date key once (avoid calling function in JSX)
  const todayKey = getTodayKey();

  useEffect(() => {
    loadCalendar();
  }, [user.id]);

  const loadCalendar = async () => {
    try {
      const cal = await getCurrentCalendar(user.id);
      setCalendar(cal);
    } catch (error) {
      console.error('[CALENDAR] Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStamp = async (reflection?: string) => {
    if (!calendar || hasStampedToday(calendar)) return;
    
    try {
      const result = await addDailyStamp(user.id, 'standard', reflection);
      await loadCalendar(); // Reload to show new stamp
      
      if (onStampAdded) {
        onStampAdded(result.xpEarned);
      }
      
      if (result.milestoneReached) {
        // Show milestone notification
        alert(t('calendar.milestone_reached', { day: result.milestoneReached }));
      }
      
      setSelectedDay(null);
      setReflectionText('');
    } catch (error) {
      console.error('[CALENDAR] Error adding stamp:', error);
    }
  };

  if (loading || !calendar) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 animate-pulse">
        <div className="h-64 bg-slate-800 rounded-2xl" />
      </div>
    );
  }

  const stats = getCalendarStats(calendar);
  const config = user.calendarConfig || {
    xpPerStamp: { standard: 20, special: 50, bonus: 100 },
    milestones: [
      { day: 5, reward: { type: 'xp_boost', value: 50 } },
      { day: 10, reward: { type: 'cosmetic', value: 'frame_basic' } },
      { day: 20, reward: { type: 'discount', value: 0.1 } },
      { day: 30, reward: { type: 'unlockable', value: 'badge_monthly_complete' } }
    ],
    bonusChance: 0.1
  };

  // Generate calendar days
  const year = calendar.year;
  const month = calendar.month;
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  
  // Extract milestone progress calculation before JSX (avoid inline IIFE)
  const nextMilestoneDay = config.milestones.length > 0
    ? (config.milestones.find(m => !calendar.milestonesReached.includes(m.day))?.day ?? daysInMonth)
    : daysInMonth;
  const progressPercentage = config.milestones.length > 0
    ? Math.min(100, Math.max(0, (stats.totalStamps / nextMilestoneDay) * 100))
    : 0;
  const startDayOfWeek = firstDay.getDay();

  const days: (DailyStamp | null)[] = [];
  
  // Add empty cells for days before month starts
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  
  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const stamp = calendar.stamps.find(s => s.date === dateKey) || null;
    days.push(stamp || null);
  }

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
  const todayDay = isCurrentMonth ? today.getDate() : null;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 rounded-2xl">
            <Calendar className="text-emerald-400" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-black uppercase">{t('calendar.title')}</h3>
            <p className="text-xs text-slate-500">
              {new Date(year, month - 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-black text-emerald-400">{stats.totalStamps}</p>
          <p className="text-[10px] text-slate-500 uppercase">{t('calendar.stamps_this_month')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-800 rounded-xl p-4 text-center">
          <p className="text-lg font-black text-cyan-400">{Math.round(stats.completionPercent)}%</p>
          <p className="text-[10px] text-slate-500 uppercase">{t('calendar.completion')}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 text-center">
          <p className="text-lg font-black text-orange-400">{stats.milestonesReached}</p>
          <p className="text-[10px] text-slate-500 uppercase">{t('calendar.milestones')}</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-4 text-center">
          <p className="text-lg font-black text-purple-400">{stats.rewardsUnused}</p>
          <p className="text-[10px] text-slate-500 uppercase">{t('calendar.rewards_available')}</p>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="space-y-4">
        {/* Day labels */}
        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-[10px] font-black text-slate-500 uppercase">
              {t(`calendar.day_${day.toLowerCase()}`)}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-2">
          {days.map((stamp, index) => {
            const dayNumber = index - startDayOfWeek + 1;
            const isToday = isCurrentMonth && todayDay === dayNumber;
            const isPast = dayNumber > 0 && dayNumber <= daysInMonth && 
              (new Date(year, month - 1, dayNumber) < new Date(today.setHours(0, 0, 0, 0)));
            const isFuture = dayNumber > 0 && dayNumber <= daysInMonth && 
              (new Date(year, month - 1, dayNumber) > new Date(today.setHours(23, 59, 59, 999)));
            
            if (dayNumber <= 0 || dayNumber > daysInMonth) {
              return <div key={index} className="aspect-square" />;
            }

            const hasStamp = stamp !== null;
            const isMilestone = config.milestones.some(m => m.day === dayNumber);
            
            return (
              <button
                key={index}
                onClick={() => {
                  if (isToday && !hasStamp) {
                    setSelectedDay(`${year}-${String(month).padStart(2, '0')}-${String(dayNumber).padStart(2, '0')}`);
                  } else if (hasStamp) {
                    setSelectedDay(stamp.date);
                  }
                }}
                className={`aspect-square rounded-xl border-2 transition-all relative ${
                  hasStamp
                    ? stamp.type === 'bonus'
                      ? 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/50'
                      : stamp.type === 'special'
                      ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/50'
                      : 'bg-emerald-500/10 border-emerald-500/50'
                    : isToday
                    ? 'bg-slate-800 border-orange-500/50 hover:border-orange-500'
                    : isPast
                    ? 'bg-slate-800/30 border-slate-700/30'
                    : 'bg-slate-800/10 border-slate-800/30 opacity-50'
                } ${isToday && !hasStamp ? 'animate-pulse cursor-pointer' : ''}`}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span className={`text-sm font-black ${
                    hasStamp ? 'text-emerald-400' : isToday ? 'text-orange-400' : 'text-slate-500'
                  }`}>
                    {dayNumber}
                  </span>
                  {hasStamp && (
                    <CheckCircle2 
                      size={16} 
                      className={`mt-1 ${
                        stamp.type === 'bonus' ? 'text-yellow-400' :
                        stamp.type === 'special' ? 'text-purple-400' :
                        'text-emerald-400'
                      }`}
                      fill="currentColor"
                    />
                  )}
                  {isMilestone && (
                    <Star 
                      size={10} 
                      className="absolute top-1 right-1 text-yellow-400"
                      fill="currentColor"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Next Milestone Progress */}
      {config.milestones.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-slate-400 uppercase">
              {t('calendar.next_milestone')}
            </span>
            <span className="text-xs font-black text-emerald-400">
              {stats.totalStamps} / {nextMilestoneDay}
            </span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Stamp Today Button */}
      {isCurrentMonth && todayDay && !hasStampedToday(calendar) && (
        <button
          onClick={() => handleAddStamp()}
          className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-black uppercase transition-all flex items-center justify-center gap-2"
        >
          <CheckCircle2 size={20} />
          {t('calendar.stamp_today')}
        </button>
      )}

      {/* Day Detail Modal */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black uppercase">
                {new Date(selectedDay).toLocaleDateString(undefined, { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <button
                onClick={() => {
                  setSelectedDay(null);
                  setReflectionText('');
                }}
                className="text-slate-500 hover:text-white"
              >
                ×
              </button>
            </div>
            
            {calendar.stamps.find(s => s.date === selectedDay) ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="text-emerald-400" size={20} fill="currentColor" />
                  <span className="font-black text-emerald-400">{t('calendar.stamped')}</span>
                </div>
                <div className="bg-slate-800 rounded-xl p-4">
                  <p className="text-sm text-slate-300">
                    {t('calendar.xp_earned')}: <span className="font-black text-emerald-400">
                      {calendar.stamps.find(s => s.date === selectedDay)?.xpEarned} XP
                    </span>
                  </p>
                  {calendar.stamps.find(s => s.date === selectedDay)?.reflection && (
                    <p className="text-sm text-slate-400 mt-2 italic">
                      "{calendar.stamps.find(s => s.date === selectedDay)?.reflection}"
                    </p>
                  )}
                </div>
              </div>
            ) : isCurrentMonth && selectedDay === todayKey ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-400">{t('calendar.add_reflection_optional')}</p>
                <textarea
                  value={reflectionText}
                  onChange={e => setReflectionText(e.target.value)}
                  placeholder={t('calendar.reflection_placeholder')}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-white resize-none"
                  rows={3}
                />
                <button
                  onClick={() => handleAddStamp(reflectionText)}
                  className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-black uppercase transition-all"
                >
                  {t('calendar.stamp_and_save')}
                </button>
              </div>
            ) : (
              <p className="text-sm text-slate-400">{t('calendar.no_stamp_this_day')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

